import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { WorkspaceTheme } from '@/types/theme'

export type ThemeMode = 'light' | 'dark' | 'system'
export type Density = 'compact' | 'comfortable' | 'spacious'

interface DeviceAppearance {
  density: Density
  accentColor: string | null
  sidebarColor: string | null
  sidebarTextColor: string | null
  contentBgColor: string | null
}

interface AppearanceState {
  scope: string | null
  workspaceTheme: WorkspaceTheme | null
  deviceOverrides: Record<string, DeviceAppearance>
  canSaveTheme: boolean
  bindWorkspace: (scope: string, theme: ThemeMode, branding: WorkspaceTheme | null, canSave: boolean) => void
  clearWorkspace: () => void
  theme: ThemeMode
  density: Density
  accentColor: string | null
  sidebarColor: string | null
  sidebarTextColor: string | null
  contentBgColor: string | null // NEW (Phase 921) — Content background hex channel

  setTheme: (theme: ThemeMode) => void
  setDensity: (density: Density) => void
  setAccentColor: (color: string | null) => void
  setSidebarColor: (bg: string | null, text?: string | null) => void
  setContentBgColor: (color: string | null) => void // NEW (Phase 921)
  resetAll: () => void
}

const defaults = {
  theme: 'light' as ThemeMode,
  density: 'comfortable' as Density,
  accentColor: null as string | null,
  sidebarColor: null as string | null,
  sidebarTextColor: null as string | null,
  contentBgColor: null as string | null, // NEW (Phase 921)
}

const deviceDefaults: DeviceAppearance = { density: 'comfortable', accentColor: null, sidebarColor: null, sidebarTextColor: null, contentBgColor: null };

export const useAppearanceStore = create<AppearanceState>()(
  persist(
    (set, get) => {
      const updateDevice = (patch: Partial<DeviceAppearance>) => {
        const { scope, deviceOverrides } = get();
        if (!scope) return;
        const values = { ...deviceDefaults, ...deviceOverrides[scope], ...patch };
        set({ ...values, deviceOverrides: { ...deviceOverrides, [scope]: values } });
      };
      return {
        ...defaults,
        scope: null,
        workspaceTheme: null,
        deviceOverrides: {},
        canSaveTheme: false,
        bindWorkspace: (scope, theme, workspaceTheme, canSaveTheme) => set({
          scope, theme, workspaceTheme, canSaveTheme, ...deviceDefaults, ...get().deviceOverrides[scope],
        }),
        clearWorkspace: () => set({ ...defaults, scope: null, workspaceTheme: null, canSaveTheme: false }),
        setTheme: (theme) => set({ theme }),
        setDensity: (density) => updateDevice({ density }),
        setAccentColor: (accentColor) => updateDevice({ accentColor }),
        setSidebarColor: (sidebarColor, sidebarTextColor) => updateDevice({ sidebarColor, sidebarTextColor: sidebarTextColor ?? null }),
        setContentBgColor: (contentBgColor) => updateDevice({ contentBgColor }),
        resetAll: () => updateDevice(deviceDefaults),
      };
    },
    {
      // Old unscoped settings cannot be attributed safely to the signed-in person.
      name: 'seer-appearance-scoped',
      partialize: (state) => ({ deviceOverrides: state.deviceOverrides }),
    }
  )
)
